import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { SealCheck } from "@phosphor-icons/react";
import { useLang } from "@/context/LangContext";
import { ALL_CATEGORIES, CATEGORY_META } from "@/data/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const CURRENCIES = ["EUR", "GBP", "PLN", "USD"];

export default function CreateListing() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { t, tc, td } = useLang();

  const [form, setForm] = useState({
    category: "cleaning",
    title: "",
    description: "",
    country: "",
    city: "",
    price_type: "hourly",
    price_min: "",
    price_max: "",
    price_fixed: "",
    price_unit: "hour",
    currency: "EUR",
    availability: [{ day: "monday", start: "09:00", end: "17:00" }],
    is_business: false,
    image: "",
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [founding, setFounding] = useState(null);

  useEffect(() => {
    const city = form.city.trim();
    if (city.length < 2 || isEdit) { setFounding(null); return; }
    const h = setTimeout(() => {
      api.get(`/founding/status?city=${encodeURIComponent(city)}`).then(({ data }) => setFounding(data)).catch(() => setFounding(null));
    }, 400);
    return () => clearTimeout(h);
  }, [form.city, isEdit]);

  useEffect(() => {
    if (isEdit) {
      api
        .get(`/listings/${id}`)
        .then(({ data }) =>
          setForm({
            ...data,
            price_min: data.price_min ?? "",
            price_max: data.price_max ?? "",
            price_fixed: data.price_fixed ?? "",
            availability: data.availability?.length
              ? data.availability
              : [{ day: "monday", start: "09:00", end: "17:00" }],
          })
        )
        .catch(() => navigate("/dashboard"));
    }
  }, [id, isEdit, navigate]);

  const addSlot = () =>
    set("availability", [...form.availability, { day: "monday", start: "09:00", end: "17:00" }]);
  const removeSlot = (i) =>
    set("availability", form.availability.filter((_, idx) => idx !== i));
  const updateSlot = (i, k, v) =>
    set("availability", form.availability.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      image: form.image || CATEGORY_META[form.category]?.image,
      price_min: form.price_type === "hourly" ? parseFloat(form.price_min) || 0 : null,
      price_max: form.price_type === "hourly" ? parseFloat(form.price_max) || null : null,
      price_fixed: form.price_type === "fixed" ? parseFloat(form.price_fixed) || 0 : null,
    };
    try {
      if (isEdit) {
        await api.patch(`/listings/${id}`, payload);
        toast.success(t("toast_saved"));
      } else {
        await api.post("/listings", payload);
        toast.success(t("toast_listing_created"));
      }
      navigate("/dashboard");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "mt-1.5 rounded-xl border-ppc bg-ppc-surface";

  return (
    <div className="mx-auto max-w-2xl px-5 pb-28 pt-10 md:pb-10 lg:px-8">
      <h1 className="font-display text-4xl font-medium tracking-tighter text-ppc-text lg:text-5xl">
        {isEdit ? t("cl_edit_title") : t("cl_title")}
      </h1>

      <form onSubmit={submit} className="mt-8 space-y-6">
        {/* Category tiles */}
        <div>
          <Label className="mb-2 block">{t("cl_cat")}</Label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {ALL_CATEGORIES.map((c) => {
              const Icon = CATEGORY_META[c].icon;
              return (
                <button
                  key={c}
                  type="button"
                  data-testid={`cl-category-${c}`}
                  onClick={() => set("category", c)}
                  className={`flex flex-col items-center gap-1.5 rounded-2xl border-2 p-3 text-center transition-colors ${
                    form.category === c
                      ? "border-primary-green bg-primary-green/5"
                      : "border-ppc bg-ppc-surface"
                  }`}
                >
                  <Icon
                    className={`h-5 w-5 ${
                      form.category === c ? "text-primary-green" : "text-ppc-muted"
                    }`}
                  />
                  <span className="text-xs font-medium leading-tight text-ppc-text">{tc(c)}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <Label htmlFor="title">{t("cl_name")}</Label>
          <Input
            id="title"
            data-testid="cl-title-input"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder={t("cl_name_ph")}
            required
            className={inputCls}
          />
        </div>

        <div>
          <Label htmlFor="desc">{t("cl_desc")}</Label>
          <Textarea
            id="desc"
            data-testid="cl-desc-input"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder={t("cl_desc_ph")}
            required
            rows={4}
            className={inputCls}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="country">{t("cl_country")}</Label>
            <Input
              id="country"
              data-testid="cl-country-input"
              value={form.country}
              onChange={(e) => set("country", e.target.value)}
              required
              className={inputCls}
            />
          </div>
          <div>
            <Label htmlFor="city">{t("cl_city")}</Label>
            <Input
              id="city"
              data-testid="cl-city-input"
              value={form.city}
              onChange={(e) => set("city", e.target.value)}
              required
              className={inputCls}
            />
          </div>
        </div>
        {founding && (
          <p className="founding-badge inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs" data-testid="founding-spots">
            <SealCheck size={14} weight="fill" />
            {founding.remaining > 0
              ? t("founding_spots").replace("{n}", founding.remaining).replace("{city}", founding.city)
              : founding.city_open
              ? t("founding_full").replace("{city}", founding.city)
              : t("founding_cities_full")}
          </p>
        )}

        {/* Pricing */}
        <div className="rounded-2xl border border-ppc bg-ppc-surface p-5">
          <Label className="mb-3 block font-display text-base font-medium">{t("cl_pricing")}</Label>
          <div className="flex gap-2">
            {[
              ["hourly", t("cl_pt_hourly")],
              ["fixed", t("cl_pt_fixed")],
            ].map(([v, label]) => (
              <button
                key={v}
                type="button"
                data-testid={`cl-pricetype-${v}`}
                onClick={() => set("price_type", v)}
                className={`flex-1 rounded-xl border-2 py-2 text-sm font-medium transition-colors ${
                  form.price_type === v
                    ? "chip-active border-primary-green text-primary-green"
                    : "border-ppc text-ppc-muted"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-3 gap-3">
            {form.price_type === "hourly" ? (
              <>
                <div>
                  <Label>{t("cl_min")}</Label>
                  <Input
                    type="number"
                    step="0.5"
                    data-testid="cl-pricemin-input"
                    value={form.price_min}
                    onChange={(e) => set("price_min", e.target.value)}
                    className={inputCls}
                  />
                </div>
                <div>
                  <Label>{t("cl_max")}</Label>
                  <Input
                    type="number"
                    step="0.5"
                    data-testid="cl-pricemax-input"
                    value={form.price_max}
                    onChange={(e) => set("price_max", e.target.value)}
                    className={inputCls}
                  />
                </div>
              </>
            ) : (
              <div>
                <Label>{t("cl_fixed")}</Label>
                <Input
                  type="number"
                  step="1"
                  data-testid="cl-pricefixed-input"
                  value={form.price_fixed}
                  onChange={(e) => set("price_fixed", e.target.value)}
                  className={inputCls}
                />
              </div>
            )}
            <div>
              <Label>{t("cl_unit")}</Label>
              <Select value={form.price_unit} onValueChange={(v) => set("price_unit", v)}>
                <SelectTrigger data-testid="cl-unit-select" className={inputCls}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="">
                  <SelectItem value="hour">{t("cl_unit_hour")}</SelectItem>
                  <SelectItem value="task">{t("cl_unit_task")}</SelectItem>
                  <SelectItem value="day">{t("cl_unit_day")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 w-32">
            <Label>{t("cl_currency")}</Label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger data-testid="cl-currency-select" className={inputCls}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="">
                {CURRENCIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="mt-4 flex items-start gap-1.5 rounded-xl bg-ppc-bg p-3 text-xs leading-relaxed text-ppc-muted" data-testid="cl-fee-note">
            <Info className="mt-0.5 h-4 w-4 shrink-0" /> {t("fee_provider_note")}
          </p>
        </div>

        {/* Availability */}
        <div className="rounded-2xl border border-ppc bg-ppc-surface p-5">
          <Label className="mb-3 block font-display text-base font-medium">{t("cl_avail")}</Label>
          <div className="space-y-3">
            {form.availability.map((slot, i) => (
              <div key={i} className="flex items-end gap-2" data-testid={`cl-slot-${i}`}>
                <div className="flex-1">
                  <Label className="text-xs">{t("cl_day")}</Label>
                  <Select value={slot.day} onValueChange={(v) => updateSlot(i, "day", v)}>
                    <SelectTrigger className="mt-1 rounded-xl border-ppc bg-ppc-bg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="">
                      {DAYS.map((d) => (
                        <SelectItem key={d} value={d}>
                          {td(d)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("cl_start")}</Label>
                  <Input
                    type="time"
                    value={slot.start}
                    onChange={(e) => updateSlot(i, "start", e.target.value)}
                    className="mt-1 rounded-xl border-ppc bg-ppc-bg"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t("cl_end")}</Label>
                  <Input
                    type="time"
                    value={slot.end}
                    onChange={(e) => updateSlot(i, "end", e.target.value)}
                    className="mt-1 rounded-xl border-ppc bg-ppc-bg"
                  />
                </div>
                {form.availability.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    data-testid={`cl-remove-slot-${i}`}
                    onClick={() => removeSlot(i)}
                    className="text-[#f0a0a8]"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            data-testid="cl-add-slot-btn"
            onClick={addSlot}
            className="mt-4 gap-1.5 rounded-xl border-ppc text-primary-green"
          >
            <Plus className="h-4 w-4" /> {t("cl_add_slot")}
          </Button>
        </div>

        <label className="flex items-center gap-3 rounded-xl border border-ppc bg-ppc-surface p-3">
          <Checkbox
            data-testid="cl-business-checkbox"
            checked={form.is_business}
            onCheckedChange={(v) => set("is_business", !!v)}
          />
          <span className="text-sm text-ppc-text">{t("cl_business")}</span>
        </label>

        <Button
          type="submit"
          disabled={busy}
          data-testid="cl-submit-btn"
          className="w-full rounded-xl btn-primary py-6"
        >
          {isEdit ? t("cl_save") : t("cl_publish")}
        </Button>
      </form>
    </div>
  );
}
